import * as Sentry from '@sentry/node';
import { APIGatewayProxyEvent, Context, CustomAuthorizerEvent } from 'aws-lambda';
import { getLoggerInstance } from './logger';

export function init() {
  const config = {
    dsn: process.env.SENTRY_DSN,
    environment: process.env.NODE_ENV,
    release: process.env.GIT_COMMIT_LONG,
    integrations: [
      new Sentry.Integrations.RewriteFrames({
        root: process.cwd(),
      }),
    ],
  };
  Sentry.init(config);
  Sentry.configureScope(scope => {
    scope.clear(); // this will clear the breadcrumbs of previous invocations
    const tags = {
      lambda: process.env.AWS_LAMBDA_FUNCTION_NAME,
      version: process.env.AWS_LAMBDA_FUNCTION_VERSION,
      memory_size: process.env.AWS_LAMBDA_FUNCTION_MEMORY_SIZE,
      log_group: process.env.AWS_LAMBDA_LOG_GROUP_NAME,
      log_stream: process.env.AWS_LAMBDA_LOG_STREAM_NAME,
      service_name: process.env.SERVERLESS_SERVICE,
      stage: process.env.SERVERLESS_STAGE,
      alias: process.env.SERVERLESS_ALIAS,
      region: process.env.SERVERLESS_REGION || process.env.AWS_REGION,
    };
    for (const [key, value] of Object.entries(tags)) {
      if (value) {
        scope.setTag(key, value);
      }
    }
  });
}

/**
 * It captures errors in Sentry with either a non 4XX status code
 * or with no status code
 */
export function captureNon4XX(error: any) {
  if (error.statusCode >= 400 && error.statusCode <= 499) {
    // ignore
    return;
  }
  captureError(error);
}

export function captureError(error: Error) {
  let errorToReport = error;
  if (typeof error.message !== 'string') {
    const message = JSON.stringify(error.message);
    errorToReport = Object.assign(new Error(), error, { message });
  }
  Sentry.captureException(errorToReport);
}

/**
 * Attaches the Lambda event object as a breadcrumb
 */
export function addInvokeBreadcrumb(params: {
  event: APIGatewayProxyEvent | CustomAuthorizerEvent;
  context: Context;
}) {
  const { event, context } = params;
  const data: { [key: string]: any } = {
    awsRequestId: context.awsRequestId,
    queryStringParameters: event.queryStringParameters,
    pathParameters: event.pathParameters,
  };
  if ('body' in event) {
    data.body = event.body;
  }
  Sentry.addBreadcrumb({
    category: 'lambda invoke',
    level: Sentry.Severity.Info,
    data,
  });
}

/**
 * This will drain the whole queue of events, returns true if queue is empty or drained.
 * If timeout is provided and the queue takes longer to drain, the promise still resolves but with false.
 */
export async function drain(timeoutInMs = 2000) {
  const logger = getLoggerInstance();
  const success = await Sentry.getCurrentHub()
    .getClient()
    .close(timeoutInMs);
  if (!success) {
    logger.warn('timed out before sending all Sentry events');
  }
  return success;
}

export const addBreadcrumb = Sentry.addBreadcrumb;
